import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { api } from '../api';

// Types
interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  apiKey?: string;
  agentApiKey?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  regenerateApiKey: () => Promise<string>;
  regenerateAgentApiKey: () => Promise<string>;
  deleteAgentApiKey: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  updateUser: async () => {},
  changePassword: async () => {},
  regenerateApiKey: async () => '',
  regenerateAgentApiKey: async () => '',
  deleteAgentApiKey: async () => {},
});

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Setup axios defaults for any remaining axios calls
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user from token
  useEffect(() => {
    const loadUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      console.log('Loading user profile with token:', token ? token.substring(0, 10) + '...' : 'none');
      
      try {
        // Use the auth API to get the user profile
        const userData = await api.auth.getProfile();
        console.log('User profile loaded:', userData);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error loading user:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }

      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Login user
  const login = async (email: string, password: string) => {
    try {
      console.log('Attempting login with API for email:', email);
      
      // Use the auth API to login
      const response = await api.auth.login(email, password);
      console.log('Login API response received:', response ? 'success' : 'empty');
      
      // Get token and user from response
      const { token, user } = response;

      if (!token || !user) {
        console.error('Login response missing token or user:', response);
        throw new Error('Invalid login response from server');
      }

      // Store in localStorage and update state
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);

      console.log('Login successful, user:', user.email);
    } catch (error: any) {
      console.error('Login error:', error);
      
      const errorMessage = error.message || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
      
      throw new Error(errorMessage);
    }
  };

  // Register user
  const register = async (name: string, email: string, password: string) => {
    try {
      console.log('Attempting to register user:', email);
      
      // Use the auth API to register
      const response = await api.auth.register(name.trim(), email, password);
      console.log('Registration response received');
      
      const { token, user } = response;

      if (!token || !user) {
        console.error('Registration response missing token or user:', response);
        throw new Error('Invalid registration response from server');
      }

      // Store in localStorage and update state
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);

      toast.success('Registration successful');
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
      throw error;
    }
  };

  // Logout user
  const logout = () => {
    console.log('Logging out user');
    
    // Clear auth data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Clear any potential session cookies
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
    
    // Force refresh of API client state by setting a timestamp
    localStorage.setItem('logout_time', Date.now().toString());
    
    // Clear authorization header
    delete axios.defaults.headers.common['Authorization'];
    
    // Update state
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    // Show logout notification
    toast.info('You have been logged out');
  };

  // Update user
  const updateUser = async (userData: Partial<User>) => {
    try {
      console.log('Updating user profile');
      
      // Use the auth API to update the user profile
      const updatedUser = await api.auth.updateProfile({
        name: userData.name,
        email: userData.email
      });
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile updated successfully');
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update profile');
      throw error;
    }
  };

  // Change password
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      console.log('Changing password');
      
      // Use the auth API to change password
      await api.auth.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
    } catch (error: any) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to change password');
      throw error;
    }
  };

  // Regenerate API key
  const regenerateApiKey = async (): Promise<string> => {
    try {
      console.log('Regenerating API key');
      
      // Use a dedicated API endpoint through our API module
      const response = await api.auth.regenerateApiKey();
      toast.success('API key regenerated successfully');
      
      // Update the user object with the new API key
      if (user) {
        const updatedUser = { ...user, apiKey: response.apiKey };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      return response.apiKey;
    } catch (error: any) {
      console.error('API key regeneration error:', error);
      toast.error(error.message || 'Failed to regenerate API key');
      throw error;
    }
  };

  // Regenerate Agent API key
  const regenerateAgentApiKey = async (): Promise<string> => {
    try {
      console.log('Regenerating Agent API key');
      
      // Use a dedicated API endpoint through our API module
      const response = await api.auth.regenerateAgentApiKey();
      toast.success('Agent API key regenerated successfully');
      
      // Update the user object with the new Agent API key
      if (user) {
        const updatedUser = { ...user, agentApiKey: response.agentApiKey };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      return response.agentApiKey;
    } catch (error: any) {
      console.error('Agent API key regeneration error:', error);
      toast.error(error.message || 'Failed to regenerate Agent API key');
      throw error;
    }
  };

  // Delete Agent API key
  const deleteAgentApiKey = async (): Promise<void> => {
    try {
      console.log('Deleting Agent API key');
      
      // Use a dedicated API endpoint through our API module
      await api.auth.deleteAgentApiKey();
      toast.success('Agent API key deleted successfully');
      
      // Update the user object by removing the Agent API key
      if (user) {
        const updatedUser = { ...user, agentApiKey: undefined };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error: any) {
      console.error('Agent API key deletion error:', error);
      toast.error(error.message || 'Failed to delete Agent API key');
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        login,
        register,
        logout,
        updateUser,
        changePassword,
        regenerateApiKey,
        regenerateAgentApiKey,
        deleteAgentApiKey,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 